import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { parseShopLocation } from './shopUtils';
import { useShopsFromDb } from './useShopsFromDb';
import { normalizeShopIds } from './shopAssignmentUtils';
import ShopPhotosEditor, { normalizePhotoUrls } from './ShopPhotosEditor';
import './ShopsPage.css';

function shopLabel(row) {
  return (row.shop && String(row.shop).trim()) || `Mağaza #${row.id}`;
}

function shortShopName(name) {
  return String(name || '')
    .replace(/^MAKE\s*ME\s*JOI\s*/i, '')
    .trim() || name;
}

function thumbFromPhotos(photos) {
  const urls = normalizePhotoUrls(photos);
  return urls[0] || null;
}

function hasValidCoords(row) {
  return Boolean(parseShopLocation(row?.location));
}

function ShopForm({
  row,
  isNew,
  staffCount,
  onSaved,
  onCreated,
  onDeleted,
  onError,
  onCancelNew,
}) {
  const loc0 = parseShopLocation(row?.location) || { lat: '41.0082', lng: '28.9784' };
  const [shop, setShop] = useState(row?.shop || '');
  const [description, setDescription] = useState(row?.description || '');
  const [lat, setLat] = useState(loc0.lat === '' ? '' : String(loc0.lat));
  const [lng, setLng] = useState(loc0.lng === '' ? '' : String(loc0.lng));
  const [mapslink, setMapslink] = useState(row?.mapslink || '');
  const [photos, setPhotos] = useState(() => normalizePhotoUrls(row?.photos));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const loc = parseShopLocation(row?.location) || { lat: '41.0082', lng: '28.9784' };
    setShop(row?.shop || '');
    setDescription(row?.description || '');
    setLat(loc.lat === '' || loc.lat == null ? '' : String(loc.lat));
    setLng(loc.lng === '' || loc.lng == null ? '' : String(loc.lng));
    setMapslink(row?.mapslink || '');
    setPhotos(normalizePhotoUrls(row?.photos));
    setSavedFlash(false);
  }, [row]);

  const savePayload = () => {
    const la = parseFloat(lat);
    const lo = parseFloat(lng);
    if (!shop.trim()) {
      onError('Mağaza adı zorunlu');
      return null;
    }
    if (!Number.isFinite(la) || !Number.isFinite(lo)) {
      onError('Enlem ve boylam geçerli sayı olmalı');
      return null;
    }
    return {
      shop: shop.trim(),
      description: description.trim() || null,
      location: { lat: la, lng: lo },
      mapslink: mapslink.trim() || null,
      photos,
    };
  };

  const save = async () => {
    const payload = savePayload();
    if (!payload || isNew) return;
    setSaving(true);
    const { data, error } = await supabase
      .from('shops')
      .update(payload)
      .eq('id', row.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      onError(error.message);
      return;
    }
    if (!data) {
      onError('Kayıt güncellenemedi. RLS/policy nedeniyle UPDATE engelleniyor olabilir.');
      return;
    }
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1800);
    onSaved(data);
  };

  const create = async () => {
    const payload = savePayload();
    if (!payload) return;
    setSaving(true);
    const { data, error } = await supabase
      .from('shops')
      .insert(payload)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      onError(error.message);
      return;
    }
    onCreated(data || null);
  };

  const del = async () => {
    if (staffCount > 0) {
      onError('Bu şubeye atanmış personel var; önce Ekip → Şube ataması menüsünden atamaları kaldırın.');
      return;
    }
    if (!window.confirm(`${shopLabel(row)} silinsin mi?`)) return;
    setDeleting(true);
    const { error } = await supabase.from('shops').delete().eq('id', row.id);
    setDeleting(false);
    if (error) {
      onError(error.message);
      return;
    }
    onDeleted(row.id);
  };

  const busy = saving || deleting;
  const mapsUrl = mapslink.trim();
  const thumb = thumbFromPhotos(photos);
  const coordsOk = Number.isFinite(parseFloat(lat)) && Number.isFinite(parseFloat(lng));

  return (
    <div className="shops-detail-form">
      <div className="shops-detail-head">
        <div className="shops-detail-head-main">
          <div className={`shops-detail-thumb${thumb ? '' : ' shops-detail-thumb--empty'}`}>
            {thumb ? (
              <img src={thumb} alt="" />
            ) : (
              <span aria-hidden>{(shop || 'M').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div>
            <h2 className="shops-detail-title">{isNew ? 'Yeni mağaza' : shopLabel(row)}</h2>
            {!isNew ? (
              <p className="shops-detail-sub">
                #{row.id}
                {' · '}
                {staffCount} personel
                {' · '}
                {photos.length} görsel
              </p>
            ) : (
              <p className="shops-detail-sub">Temel bilgileri doldurup kaydedin.</p>
            )}
            {!isNew ? (
              <div className="shops-chip-row">
                <span className={`shops-chip${coordsOk ? ' shops-chip--ok' : ' shops-chip--warn'}`}>
                  {coordsOk ? 'Konum hazır' : 'Konum eksik'}
                </span>
                <span className={`shops-chip${mapsUrl ? ' shops-chip--ok' : ''}`}>
                  {mapsUrl ? 'Maps linki var' : 'Maps linki yok'}
                </span>
                <span className={`shops-chip${photos.length ? ' shops-chip--ok' : ''}`}>
                  {photos.length ? `${photos.length} görsel` : 'Görsel yok'}
                </span>
                <span className={`shops-chip${staffCount > 0 ? ' shops-chip--accent' : ''}`}>
                  {staffCount > 0 ? `${staffCount} personel` : 'Personel yok'}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="shops-detail-head-actions">
          {!isNew && mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shops-maps-link"
            >
              Haritada aç
            </a>
          ) : null}
          {savedFlash ? <span className="shops-saved-flash">Kaydedildi</span> : null}
        </div>
      </div>

      <section className="shops-form-section">
        <h3 className="shops-form-section-title">Temel bilgiler</h3>
        <label className="shops-field">
          <span className="shops-field-label">Mağaza adı</span>
          <input
            className="shops-input"
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            placeholder="Örn. MAKE ME JOI YEŞİLKÖY"
            disabled={busy}
          />
        </label>
        <label className="shops-field">
          <span className="shops-field-label">Açıklama</span>
          <textarea
            className="shops-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kısa açıklama (isteğe bağlı)"
            rows={3}
            disabled={busy}
          />
        </label>
      </section>

      <section className="shops-form-section">
        <h3 className="shops-form-section-title">Konum</h3>
        <div className="shops-coords-row">
          <label className="shops-field shops-field--half">
            <span className="shops-field-label">Enlem</span>
            <input
              className="shops-input"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              inputMode="decimal"
              disabled={busy}
            />
          </label>
          <label className="shops-field shops-field--half">
            <span className="shops-field-label">Boylam</span>
            <input
              className="shops-input"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              inputMode="decimal"
              disabled={busy}
            />
          </label>
        </div>
        <label className="shops-field">
          <span className="shops-field-label">Google Maps linki</span>
          <input
            className="shops-input"
            value={mapslink}
            onChange={(e) => setMapslink(e.target.value)}
            placeholder="https://maps.google.com/…"
            disabled={busy}
          />
        </label>
        {coordsOk ? (
          <p className="shops-coords-hint">
            {parseFloat(lat).toFixed(5)}, {parseFloat(lng).toFixed(5)}
            {' — dashboard haritasında bu pin kullanılır.'}
          </p>
        ) : (
          <p className="shops-coords-hint shops-coords-hint--warn">
            Geçerli enlem / boylam girin; aksi halde haritada görünmez.
          </p>
        )}
      </section>

      <section className="shops-form-section">
        <h3 className="shops-form-section-title">Görseller</h3>
        <ShopPhotosEditor
          value={photos}
          onChange={setPhotos}
          onError={onError}
          disabled={busy}
        />
      </section>

      {!isNew && staffCount > 0 ? (
        <p className="shops-staff-note">
          Bu şubeye <strong>{staffCount}</strong> personel atanmış. Silmeden önce Ekip → Şube atamasından kaldırın.
        </p>
      ) : null}

      <div className="shops-form-actions">
        {isNew ? (
          <>
            <button type="button" className="shops-btn shops-btn--primary" disabled={busy || !shop.trim()} onClick={create}>
              {saving ? 'Ekleniyor…' : 'Mağaza ekle'}
            </button>
            <button type="button" className="shops-btn shops-btn--ghost" disabled={busy} onClick={onCancelNew}>
              İptal
            </button>
          </>
        ) : (
          <>
            <button type="button" className="shops-btn shops-btn--primary" disabled={busy} onClick={save}>
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
            <button type="button" className="shops-btn shops-btn--danger" disabled={busy || staffCount > 0} onClick={del}>
              {deleting ? 'Siliniyor…' : 'Sil'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ShopsPage() {
  const location = useLocation();
  const { rawRows, loading, error, reload } = useShopsFromDb();
  const [staffRows, setStaffRows] = useState([]);
  const [pageError, setPageError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [filter, setFilter] = useState('all'); // all | staff | photos | maps | nocoords

  const loadStaff = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from('staff_directory')
      .select('id, assigned_shop_ids');
    if (qErr) {
      setStaffRows([]);
      return;
    }
    setStaffRows(data || []);
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const staffCountByShopId = useMemo(() => {
    const counts = {};
    staffRows.forEach((r) => {
      normalizeShopIds(r.assigned_shop_ids).forEach((id) => {
        counts[id] = (counts[id] || 0) + 1;
      });
    });
    return counts;
  }, [staffRows]);

  const stats = useMemo(() => {
    let withStaff = 0;
    let withPhotos = 0;
    let withMaps = 0;
    let missingCoords = 0;
    rawRows.forEach((row) => {
      if ((staffCountByShopId[String(row.id)] ?? 0) > 0) withStaff += 1;
      if (normalizePhotoUrls(row.photos).length) withPhotos += 1;
      if (String(row.mapslink || '').trim()) withMaps += 1;
      if (!hasValidCoords(row)) missingCoords += 1;
    });
    return {
      total: rawRows.length,
      withStaff,
      withPhotos,
      withMaps,
      missingCoords,
    };
  }, [rawRows, staffCountByShopId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    let rows = rawRows;
    if (filter === 'staff') {
      rows = rows.filter((r) => (staffCountByShopId[String(r.id)] ?? 0) > 0);
    } else if (filter === 'photos') {
      rows = rows.filter((r) => normalizePhotoUrls(r.photos).length > 0);
    } else if (filter === 'maps') {
      rows = rows.filter((r) => String(r.mapslink || '').trim());
    } else if (filter === 'nocoords') {
      rows = rows.filter((r) => !hasValidCoords(r));
    }
    if (q) {
      rows = rows.filter((row) => {
        const name = shopLabel(row).toLocaleLowerCase('tr-TR');
        const desc = String(row.description || '').toLocaleLowerCase('tr-TR');
        return name.includes(q) || desc.includes(q) || String(row.id).includes(q);
      });
    }
    return [...rows].sort((a, b) =>
      shopLabel(a).localeCompare(shopLabel(b), 'tr', { sensitivity: 'base' }),
    );
  }, [rawRows, search, filter, staffCountByShopId]);

  const shopFromUrl = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('shop');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [location.search]);

  useEffect(() => {
    if (addingNew) return;
    if (!rawRows.length) {
      setSelectedId(null);
      return;
    }
    if (shopFromUrl != null && rawRows.some((r) => r.id === shopFromUrl)) {
      setSelectedId(shopFromUrl);
      return;
    }
    if (selectedId == null || !rawRows.some((r) => r.id === selectedId)) {
      setSelectedId(rawRows[0].id);
    }
  }, [rawRows, selectedId, addingNew, shopFromUrl]);

  const selectedRow = useMemo(
    () => rawRows.find((r) => r.id === selectedId) || null,
    [rawRows, selectedId],
  );

  const handleSaved = () => {
    setPageError('');
    reload();
  };

  const handleDeleted = (id) => {
    setPageError('');
    if (selectedId === id) setSelectedId(null);
    reload();
    loadStaff();
  };

  const handleCreated = (data) => {
    setPageError('');
    setAddingNew(false);
    if (data?.id != null) setSelectedId(data.id);
    reload();
  };

  const err = pageError || error;

  const filterTabs = [
    { id: 'all', label: 'Tümü', count: stats.total },
    { id: 'staff', label: 'Personelli', count: stats.withStaff },
    { id: 'photos', label: 'Görselli', count: stats.withPhotos },
    { id: 'maps', label: 'Maps', count: stats.withMaps },
    { id: 'nocoords', label: 'Konumsuz', count: stats.missingCoords },
  ];

  return (
    <div className="admin-page-content shops-page">
      <header className="shops-hero">
        <div className="shops-hero-copy">
          <p className="shops-kicker">Uygulama yönetimi</p>
          <h1 className="shops-title">Mağazalar</h1>
          <p className="shops-sub">
            Şube adı, konum, Maps linki ve görseller. Dashboard haritası ve uygulama şube listesi buradan beslenir.
          </p>
        </div>
        <div className="shops-hero-actions">
          <button
            type="button"
            className="shops-btn shops-btn--primary"
            onClick={() => { setAddingNew(true); setSelectedId(null); }}
            disabled={loading}
          >
            + Yeni mağaza
          </button>
          <button
            type="button"
            className="shops-btn shops-btn--ghost"
            onClick={() => { reload(); loadStaff(); }}
            disabled={loading}
          >
            {loading ? 'Yükleniyor…' : 'Yenile'}
          </button>
        </div>
      </header>

      <div className="shops-summary">
        <div className="shops-stat">
          <strong>{loading ? '…' : stats.total}</strong>
          <span>Toplam</span>
        </div>
        <div className="shops-stat">
          <strong>{loading ? '…' : stats.withStaff}</strong>
          <span>Personelli</span>
        </div>
        <div className="shops-stat">
          <strong>{loading ? '…' : stats.withPhotos}</strong>
          <span>Görselli</span>
        </div>
        <div className="shops-stat">
          <strong>{loading ? '…' : stats.withMaps}</strong>
          <span>Maps linki</span>
        </div>
        <div className={`shops-stat${stats.missingCoords ? ' shops-stat--warn' : ''}`}>
          <strong>{loading ? '…' : stats.missingCoords}</strong>
          <span>Konumsuz</span>
        </div>
      </div>

      {err ? <p className="shops-alert shops-alert--error">{err}</p> : null}

      <div className="shops-layout">
        <aside className="shops-list-panel">
          <div className="shops-search-wrap">
            <svg className="shops-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="search"
              className="shops-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Mağaza ara…"
              aria-label="Mağaza ara"
            />
          </div>

          <div className="shops-filter-tabs" role="tablist" aria-label="Filtre">
            {filterTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={filter === tab.id}
                className={`shops-filter-tab${filter === tab.id ? ' is-active' : ''}`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label}
                <em>{tab.count}</em>
              </button>
            ))}
          </div>

          <p className="shops-list-meta">
            {loading ? 'Yükleniyor…' : `${filteredRows.length} mağaza`}
          </p>

          <div className="shops-list" role="list">
            {loading ? (
              <div className="shops-list-skel" aria-hidden>
                <div /><div /><div />
              </div>
            ) : filteredRows.length === 0 ? (
              <p className="shops-list-empty">
                {rawRows.length ? 'Filtre / arama sonucu yok.' : 'Henüz mağaza yok.'}
              </p>
            ) : (
              filteredRows.map((row) => {
                const photoCount = normalizePhotoUrls(row.photos).length;
                const staffCount = staffCountByShopId[String(row.id)] ?? 0;
                const active = !addingNew && selectedId === row.id;
                const thumb = thumbFromPhotos(row.photos);
                const name = shopLabel(row);
                const coordsOk = hasValidCoords(row);
                return (
                  <button
                    key={row.id}
                    type="button"
                    role="listitem"
                    className={`shops-list-item${active ? ' is-active' : ''}`}
                    onClick={() => { setAddingNew(false); setSelectedId(row.id); }}
                  >
                    <span className={`shops-list-thumb${thumb ? '' : ' shops-list-thumb--empty'}`}>
                      {thumb ? <img src={thumb} alt="" /> : <span>{name.charAt(0)}</span>}
                    </span>
                    <span className="shops-list-item-body">
                      <span className="shops-list-item-name">{shortShopName(name)}</span>
                      {row.description ? (
                        <span className="shops-list-item-desc">{row.description}</span>
                      ) : (
                        <span className="shops-list-item-desc shops-list-item-desc--muted">Açıklama yok</span>
                      )}
                      <span className="shops-list-item-meta">
                        <span className={staffCount ? 'is-on' : ''}>{staffCount || 0} personel</span>
                        <span className={photoCount ? 'is-on' : ''}>{photoCount || 0} görsel</span>
                        <span className={coordsOk ? 'is-on' : 'is-warn'}>{coordsOk ? 'pin' : 'konum yok'}</span>
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <main className="shops-detail-panel">
          {addingNew ? (
            <ShopForm
              row={null}
              isNew
              staffCount={0}
              onCreated={handleCreated}
              onError={(m) => setPageError(m)}
              onCancelNew={() => {
                setAddingNew(false);
                if (rawRows.length) setSelectedId(rawRows[0].id);
              }}
            />
          ) : loading ? (
            <div className="shops-detail-placeholder">
              <div className="shops-detail-skel" aria-hidden />
              <p>Yükleniyor…</p>
            </div>
          ) : selectedRow ? (
            <ShopForm
              key={selectedRow.id}
              row={selectedRow}
              staffCount={staffCountByShopId[String(selectedRow.id)] ?? 0}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              onError={(m) => setPageError(m)}
            />
          ) : (
            <div className="shops-detail-placeholder">
              <p>Mağaza seçin veya yeni ekleyin.</p>
              <button
                type="button"
                className="shops-btn shops-btn--primary"
                onClick={() => setAddingNew(true)}
              >
                + Yeni mağaza
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
